import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LuMegaphone,
  LuSparkles,
  LuTriangleAlert,
  LuWrench,
  LuLightbulb,
  LuX,
  LuArrowRight,
  LuCheck,
} from 'react-icons/lu';
import './InAppMessageModal.css';

const TYPE_CONFIG = {
  announcement: {
    label: 'Platform Announcement',
    icon: LuMegaphone,
    badgeClass: 'badge-announcement',
  },
  feature: {
    label: 'Feature Update',
    icon: LuSparkles,
    badgeClass: 'badge-feature',
  },
  alert: {
    label: 'Important Notice',
    icon: LuTriangleAlert,
    badgeClass: 'badge-alert',
  },
  maintenance: {
    label: 'Scheduled Maintenance',
    icon: LuWrench,
    badgeClass: 'badge-maintenance',
  },
  tip: {
    label: 'Platform Tip',
    icon: LuLightbulb,
    badgeClass: 'badge-tip',
  },
};

export default function InAppMessageModal({
  isOpen,
  message,
  onDismiss,
  isPreview = false,
}) {
  const navigate = useNavigate();

  const handleDismiss = useCallback(
    (interactedWithCta = false) => {
      if (onDismiss) {
        onDismiss({ interactedWithCta });
      }
    },
    [onDismiss]
  );

  const handleCtaClick = () => {
    if (!message) return;
    const url = message.cta_url ? message.cta_url.trim() : '';

    handleDismiss(true);

    if (url && !isPreview) {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    }
  };

  useEffect(() => {
    if (!isOpen || isPreview) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDismiss(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isPreview, handleDismiss]);

  if (!isOpen || !message) return null;

  const typeConfig = TYPE_CONFIG[message.message_type] || TYPE_CONFIG.announcement;
  const TypeIcon = typeConfig.icon;
  const isUrgent = message.priority === 'urgent' || message.priority === 'high';

  return (
    <div
      className={`iam-modal-overlay ${isPreview ? 'is-preview' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPreview) {
          handleDismiss(false);
        }
      }}
      role="presentation"
    >
      <div
        className="iam-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="iam-modal-title"
      >
        {/* Glow backdrop accent */}
        <div className={`iam-modal-glow ${typeConfig.badgeClass}`} />

        <div className="iam-modal-header">
          <div className="iam-header-tags">
            <span className={`iam-badge ${typeConfig.badgeClass}`}>
              <TypeIcon size={14} className="iam-badge-icon" />
              {typeConfig.label}
            </span>
            {isUrgent && (
              <span className="iam-priority-badge">
                {message.priority === 'urgent' ? 'Urgent' : 'Priority'}
              </span>
            )}
            {isPreview && <span className="iam-preview-badge">Admin Preview</span>}
          </div>

          <button
            type="button"
            className="iam-close-btn"
            onClick={() => handleDismiss(false)}
            aria-label="Close notification"
            title="Dismiss message"
          >
            <LuX size={18} />
          </button>
        </div>

        <div className="iam-modal-body">
          <h2 id="iam-modal-title" className="iam-title">
            {message.title}
          </h2>

          <div className="iam-content-text">
            {String(message.message || '')
              .split('\n')
              .map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
          </div>
        </div>

        <div className="iam-modal-footer">
          <button
            type="button"
            className="iam-btn iam-btn-secondary"
            onClick={() => handleDismiss(false)}
          >
            <LuCheck size={16} />
            Got it
          </button>

          {message.cta_label && (
            <button
              type="button"
              className="iam-btn iam-btn-primary"
              onClick={handleCtaClick}
            >
              <span>{message.cta_label}</span>
              <LuArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
